import { useState } from "react";
import { COLORS, BG_BASE } from "../../utils/colors";
import { ClickableName } from "../common/EmployeeNameLink";

const sampleData = {
  teamScore: 7.1,
  totalRequiredSkills: 14,
  reinforcedSkills: [
    { skill: "Kubernetes",      requiredBy: "DevOps Engineer",  reinforcers: ["Todd Korando", "Michael Monske"] },
    { skill: "Python",          requiredBy: "SRE",              reinforcers: ["Michael Monske", "Shirley Cortez"] },
    { skill: "AWS",             requiredBy: "DevOps Engineer",  reinforcers: ["Todd Korando", "Shirley Cortez"] },
    { skill: "PostgreSQL",      requiredBy: "SQL Developer",    reinforcers: ["Shirley Cortez", "Todd Korando"] },
    { skill: "Terraform",       requiredBy: "DevOps Engineer",  reinforcers: ["Todd Korando", "Michael Monske"] },
  ],
  coveredSkills: ["CI/CD Pipelines", "Data Migration", "ETL", "Performance Tuning", "Incident Response", "Monitoring", "Linux"],
  backstops: [
    { gap_skill: "Service Mesh",   required_by_role: "DevOps Engineer", backstop_person: "Michael Monske", backstop_confidence: "medium" },
    { gap_skill: "Cloud Security", required_by_role: "SRE",             backstop_person: "Todd Korando",   backstop_confidence: "high"   },
  ],
  uncoveredGaps: ["FinOps", "Real-time Streaming"],
  mentoringPairs: [
    { mentor: "Todd Korando",   mentee: "Shirley Cortez", skill: "Kubernetes Basics",   rationale: "Cortez's ETL work will intersect with K8s pipelines — Korando can onboard quickly" },
    { mentor: "Michael Monske", mentee: "Todd Korando",   skill: "Advanced Monitoring", rationale: "Monske's SRE depth can uplift Korando's observability stack knowledge" },
  ],
};


function BackstopLine({ backstop }) {
  const conf = {
    high:   { color: COLORS.green, label: "HIGH" },
    medium: { color: COLORS.amber, label: "MED"  },
    low:    { color: COLORS.red,   label: "LOW"  },
  }[backstop.backstop_confidence] || { color: COLORS.textMuted, label: "?" };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 12px", background: BG_BASE, borderRadius: 4, marginBottom: 6 }}>
      <span style={{ fontSize: 11, fontWeight: 600, color: COLORS.text, flex: 1 }}>{backstop.gap_skill}</span>
      <span style={{ fontSize: 10, color: COLORS.textMuted }}>({backstop.required_by_role})</span>
      <span style={{ fontSize: 10, color: COLORS.textMuted }}>→ backstopped by</span>
      <span style={{ fontSize: 11, color: COLORS.purple }}>{backstop.backstop_person}</span>
      <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 6px", borderRadius: 8, background: `${conf.color}20`, color: conf.color }}>{conf.label}</span>
    </div>
  );
}

export default function TeamSynergy({ data, onEmployeeClick }) {
  const d = data || sampleData;
  const [showInfo, setShowInfo] = useState(false);

  const scoreColor = d.teamScore >= 8 ? COLORS.green : d.teamScore >= 5 ? COLORS.amber : COLORS.red;
  const reinforcedCount = (d.reinforcedSkills || []).length;
  const total = d.totalRequiredSkills || 0;

  return (
    <div style={{ background: COLORS.cardBg, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 8, padding: 20 }}>

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, color: COLORS.text, margin: 0, textTransform: "uppercase", letterSpacing: 1 }}>Team Technical Synergy</h3>
            <div style={{ position: "relative", display: "inline-block" }}>
              <span
                onMouseEnter={() => setShowInfo(true)}
                onMouseLeave={() => setShowInfo(false)}
                style={{ fontSize: 13, color: COLORS.textMuted, cursor: "help", userSelect: "none" }}
              >ⓘ</span>
              {showInfo && (
                <div style={{
                  position: "absolute", left: 0, top: 22, zIndex: 100,
                  background: "#2A2A2A", border: `1px solid ${COLORS.cardBorder}`,
                  borderRadius: 8, padding: "12px 14px", width: 300,
                  fontSize: 11, color: COLORS.textMuted, lineHeight: 1.7,
                  boxShadow: "0 4px 16px rgba(0,0,0,0.6)"
                }}>
                  <div style={{ fontWeight: 700, color: COLORS.text, marginBottom: 8, fontSize: 12 }}>How the score works</div>
                  <div>Each required skill across all roles is scored:</div>
                  <div style={{ marginTop: 4 }}><span style={{ color: COLORS.green }}>Reinforced (2 pts)</span> — 2+ team members hold this skill, even if only one role requires it</div>
                  <div style={{ marginTop: 4 }}><span style={{ color: COLORS.amber }}>Covered (1 pt)</span> — exactly one person holds it (adequate but fragile)</div>
                  <div style={{ marginTop: 4 }}><span style={{ color: COLORS.red }}>Gap (0 pts)</span> — no one on the team has it</div>
                  <div style={{ marginTop: 8, borderTop: `1px solid ${COLORS.cardBorder}`, paddingTop: 8 }}>
                    Score = total points ÷ max possible × 10. Full coverage with full reinforcement = 10.
                  </div>
                </div>
              )}
            </div>
          </div>
          <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 4 }}>Cross-role skill reinforcement across the team</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 10, color: COLORS.textMuted, textTransform: "uppercase" }}>Team Score</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: scoreColor }}>{d.teamScore}</div>
          <div style={{ fontSize: 10, color: COLORS.textMuted }}>out of 10</div>
        </div>
      </div>

      {/* Score bar + summary */}
      <div style={{ width: "100%", height: 8, background: BG_BASE, borderRadius: 4, overflow: "hidden", marginBottom: 6 }}>
        <div style={{ width: `${d.teamScore * 10}%`, height: "100%", background: `linear-gradient(90deg, ${scoreColor}60, ${scoreColor})`, borderRadius: 4 }} />
      </div>
      {total > 0 && (
        <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 20 }}>
          <span style={{ color: COLORS.green, fontWeight: 600 }}>{reinforcedCount} reinforced</span>
          {" · "}
          <span style={{ color: COLORS.amber, fontWeight: 600 }}>{(d.coveredSkills || []).length} single-coverage</span>
          {" · "}
          <span style={{ color: COLORS.red, fontWeight: 600 }}>{(d.backstops || []).length + (d.uncoveredGaps || []).length} gaps</span>
          {" "}of {total} required skills
        </div>
      )}

      {/* Reinforced Skills */}
      {(d.reinforcedSkills || []).length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.green, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>
            Reinforced Skills
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ fontSize: 10, fontWeight: 600, color: COLORS.textMuted, textAlign: "left", padding: "3px 10px 6px 10px", width: "30%" }}>Skill</th>
                <th style={{ fontSize: 10, fontWeight: 600, color: COLORS.textMuted, textAlign: "left", padding: "3px 10px 6px 10px", width: "25%" }}>Required by</th>
                <th style={{ fontSize: 10, fontWeight: 600, color: COLORS.textMuted, textAlign: "left", padding: "3px 10px 6px 10px" }}>Also held by</th>
              </tr>
            </thead>
            <tbody>
              {d.reinforcedSkills.map((item, i) => (
                <tr key={i} style={{ background: i % 2 === 0 ? `${COLORS.green}06` : "transparent" }}>
                  <td style={{ fontSize: 11, fontWeight: 600, color: COLORS.green, padding: "5px 10px" }}>{item.skill}</td>
                  <td style={{ fontSize: 11, color: COLORS.text, padding: "5px 10px" }}>{item.requiredBy}</td>
                  <td style={{ fontSize: 11, color: COLORS.green, padding: "5px 10px" }}>{item.reinforcers.join(", ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Single Coverage */}
      {(d.coveredSkills || []).length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>
            Single Coverage <span style={{ fontSize: 10, fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>— fragile, no redundancy</span>
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {d.coveredSkills.map((s, i) => (
              <span key={i} style={{ fontSize: 10, padding: "3px 10px", borderRadius: 10, background: `${COLORS.amber}15`, color: COLORS.amber, fontWeight: 500 }}>{s}</span>
            ))}
          </div>
        </div>
      )}

      {/* Gap Backstops */}
      {(d.backstops || []).length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Gap Backstops</div>
          {d.backstops.map((b, i) => <BackstopLine key={i} backstop={b} />)}
        </div>
      )}

      {/* Uncovered Gaps */}
      {(d.uncoveredGaps || []).length > 0 && (
        <div style={{ marginBottom: 16, padding: "10px 14px", background: `${COLORS.red}08`, borderRadius: 6, borderLeft: `3px solid ${COLORS.red}` }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.red, marginBottom: 6 }}>Uncovered Gaps</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {d.uncoveredGaps.map((g, i) => (
              <span key={i} style={{ fontSize: 10, padding: "3px 10px", borderRadius: 10, background: `${COLORS.red}20`, color: COLORS.red, fontWeight: 500 }}>{g}</span>
            ))}
          </div>
        </div>
      )}

      {/* Mentoring Opportunities */}
      {(d.mentoringPairs || []).length > 0 && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Mentoring Opportunities</div>
          {d.mentoringPairs.map((mp, i) => (
            <div key={i} style={{ padding: "8px 12px", background: BG_BASE, borderRadius: 4, borderLeft: `3px solid ${COLORS.teal}`, marginBottom: 6 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                <ClickableName name={mp.mentor} onClick={onEmployeeClick} style={{ fontSize: 11, fontWeight: 600, color: COLORS.teal }} />
                <span style={{ fontSize: 10, color: COLORS.textMuted }}>→ mentors</span>
                <ClickableName name={mp.mentee} onClick={onEmployeeClick} style={{ fontSize: 11, fontWeight: 600, color: COLORS.purple }} />
                <span style={{ fontSize: 10, color: COLORS.textMuted }}>on</span>
                <span style={{ fontSize: 10, padding: "1px 6px", borderRadius: 8, background: `${COLORS.teal}20`, color: COLORS.teal }}>{mp.skill}</span>
              </div>
              <div style={{ fontSize: 11, color: COLORS.textMuted, lineHeight: 1.4 }}>{mp.rationale}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
